import React, { FunctionComponent, useState, useEffect, InputHTMLAttributes } from 'react';
import classNames from 'classnames';
import { InputWrapper, getFieldA11yProps } from '../InputWrapper';
import { useIsMountedRef } from '../../hooks/useIsMountedRef';
// Import Local Styles
import styles from './input.module.css';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  customWrapperClass?: string;
  warning?: boolean;
  valid?: boolean;
  focus?: boolean;
  label?: string;
  id?: string;
  inputRef?: React.RefObject<HTMLInputElement> | null;
  showHint?: boolean;
  index?: boolean;
}

export const Input: FunctionComponent<InputProps> = (props) => {
  const autoComplete = props.autoComplete ? props.autoComplete : 'on';
  const {
    warning,
    label,
    id,
    error,
    onChange,
    valid,
    inputRef,
    value,
    maxLength = 100,
    focus,
    customWrapperClass,
    showHint,
    index,
    ...rest
  } = props;

  const fieldId = id || props.name;
  const [inputValue, setInputValue] = useState(value || (props.type === 'number' ? 0 : ''));

  const [isFocus, setIsFocus] = useState(!!focus);
  const isMountedRef = useIsMountedRef();
  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isMountedRef.current) {
      setIsFocus(true);
      if (props.onFocus) {
        props.onFocus(e);
      }
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isMountedRef.current) {
      setIsFocus(false);
      if (props.onBlur) {
        props.onBlur(e);
      }
    }
  };

  const inputClassList = classNames({
    [styles.input]: true,
    [styles.focus]: isFocus,
    [styles.active]: value && value,
    [styles.valid]: !error && value,
    [styles.error]: error && error,
  });

  useEffect(() => {
    setInputValue(value || (props.type === 'number' ? 0 : ''));
  }, [value, props.type]);

  return (
    <InputWrapper {...props} id={fieldId} customWrapperClass={customWrapperClass}>
      <input
        {...{
          autoComplete,
          ...rest,
        }}
        id={fieldId}
        {...getFieldA11yProps({ id: fieldId, error, showHint })}
        className={inputClassList}
        onChange={(e) => {
          const value = e.target.value;
          if (props.type === 'tel' && value && !value.match('^[0-9-()+ ]*$')) return;
          setInputValue(value);
          if (onChange) {
            onChange(e);
          }
        }}
        ref={inputRef}
        value={inputValue}
        maxLength={maxLength}
        onBlur={(e) => {
          e.persist();
          setIsFocus(false);
          onBlur(e);
        }}
        onFocus={(e) => {
          e.persist();
          setIsFocus(true);
          onFocus(e);
        }}
      />
    </InputWrapper>
  );
};
